import React, { useCallback } from 'react';
import { PencilSimple, Trash } from '@phosphor-icons/react';
import { Button } from '../../../../Utility/Button';
import { PaginationListType } from '../../../../../Types/PaginationParams';
import AdminViewContent from '../../../../Utility/AdminViewContent';
import AdminViewHeader from '../../../../Utility/AdminViewHeader';
import { PaginationWrapper } from '../../../../Utility/PaginationWrapper';
import { mcpService } from '../../../../../Services/mcpService';
import SearchFilter from '../../../../Utility/SearchFilter';
import useSearchFilter from '../../../../../Hooks/useSearchFilter';
import useMcpConfigs from '../../../../../Hooks/useMcpConfigs';

interface McpConfig {
    identifier: string;
    title: string;
    description: string;
    serverUrl: string;
    transport: string;
    authType: string;
    lastSync: string | null;
    lastError: string | null;
    toolsCount: number;
}

const McpConfigList: React.FC = () => {
    const { settings, handleOpenConfirmDialog, i18n, windowWidth, getBreakpoint, handleEdit, handleCreateNew } =
        useMcpConfigs();

    const { query, localQuery, searchActions } = useSearchFilter({
        initialQuery: null,
    });

    const getConfigsWrapper = useCallback(
        async (page: number, itemsPerPage: number, optionalParams?: Record<string, string>, query?: string | null) => {
            const configs = await mcpService.getConfigs(settings.demo, page, itemsPerPage, optionalParams, query);
            return configs;
        },
        [settings.demo],
    );

    const GRID_COLS =
        getBreakpoint('lg') > windowWidth
            ? 'grid-cols-[minmax(130px,300px),minmax(170px,300px),minmax(230px,1fr)]'
            : 'grid-cols-[minmax(130px,300px),minmax(130px,300px),minmax(150px,250px),minmax(150px,250px),minmax(230px,1fr)]';

    return (
        <div className="">
            <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center mb-4">
                <div>
                    <h2 className="text-xl font-semibold text-layout-textcolor">
                        {i18n.translate({ id: 'configs.title', sourceName: 'MCP' })}
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                        {i18n.translate({ id: 'configs.subtitle', sourceName: 'MCP' })}
                    </p>
                </div>
            </div>

            <div className="w-full max-w-md">
                <SearchFilter
                    value={localQuery}
                    onChange={searchActions.setLocalQuery}
                    onApply={searchActions.applyQuery}
                    onClear={searchActions.clearQuery}
                />
            </div>

            <div className="mb-4 md:hidden">
                <Button className="w-full mb-2" onClick={handleCreateNew}>
                    {i18n.translate({ id: 'configs.create', sourceName: 'MCP' })}
                </Button>
            </div>

            <div className="hidden md:block">
                <AdminViewHeader
                    gridCols={GRID_COLS}
                    cols={{
                        cols: [
                            {
                                text: i18n.translate({ id: 'configs.table.title', sourceName: 'MCP' }),
                            },
                            {
                                text: i18n.translate({ id: 'configs.table.serverUrl', sourceName: 'MCP' }),
                            },
                            {
                                text: i18n.translate({ id: 'configs.table.transport', sourceName: 'MCP' }),
                                hideCol: getBreakpoint('lg') > windowWidth,
                            },
                            {
                                text: i18n.translate({ id: 'configs.table.status', sourceName: 'MCP' }),
                                hideCol: getBreakpoint('lg') > windowWidth,
                            },
                        ],
                        button: {
                            text: i18n.translate({ id: 'configs.create', sourceName: 'MCP' }),
                            function: () => handleCreateNew(),
                        },
                    }}
                />
                <hr className="border-layout-borderColor mt-5" />
            </div>

            <PaginationWrapper
                listType={PaginationListType.McpConfigs}
                itemsFunction={getConfigsWrapper}
                itemsPerPage={10}
                query={query ?? null}
                renderItem={(config: McpConfig, index: number) => (
                    <AdminViewContent
                        key={config.identifier}
                        mobile={windowWidth < getBreakpoint('md')}
                        identifier={config.identifier}
                        gridCols={GRID_COLS}
                        cols={{
                            optionalCols: [
                                {
                                    content: (
                                        <div className="flex flex-col">
                                            <div>{config.title}</div>
                                            <div className="text-sm text-gray-500 line-clamp-3 overflow-hidden whitespace-normal break-words">
                                                {config.description}
                                            </div>
                                            {config.lastError && (
                                                <div className="text-xs text-red-500 mt-1">
                                                    {i18n.translate({ id: 'configs.error', sourceName: 'MCP' })}:{' '}
                                                    {config.lastError}
                                                </div>
                                            )}
                                        </div>
                                    ),
                                },
                                {
                                    content: <span>{config.serverUrl}</span>,
                                    hideCol: getBreakpoint('md') > windowWidth,
                                },
                                {
                                    content: <span>{config.transport.toUpperCase()}</span>,
                                    hideCol: getBreakpoint('lg') > windowWidth,
                                },
                                {
                                    content: (
                                        <div className="flex flex-col gap-1">
                                            <span className="text-xs text-layout-textcolor/70">
                                                {config.toolsCount}{' '}
                                                {i18n.translate({ id: 'configs.tools', sourceName: 'MCP' })}
                                            </span>
                                        </div>
                                    ),
                                    hideCol: getBreakpoint('lg') > windowWidth,
                                },
                            ],
                            button: {
                                buttons: [
                                    {
                                        variant: !settings.demo ? 'primaryRound' : 'disabledRound',
                                        onClick: () => {
                                            if (!settings.demo) {
                                                handleEdit(config);
                                            }
                                        },
                                        icon: <PencilSimple />,
                                        tooltipText: i18n.translate({ id: 'common.edit', sourceName: 'MCP' }),
                                    },
                                    {
                                        variant: !settings.demo ? 'secondaryRound' : 'disabledRound',
                                        onClick: () => {
                                            if (!settings.demo) {
                                                handleOpenConfirmDialog(config.identifier, config.title);
                                            }
                                        },
                                        icon: <Trash />,
                                        tooltipText: i18n.translate({ id: 'common.delete', sourceName: 'MCP' }),
                                    },
                                ],
                            },
                        }}
                    />
                )}
            />
        </div>
    );
};

export default McpConfigList;
